import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';

import { useModals } from '@any-ui-react/modals';

type Blocker = ReturnType<typeof useBlocker>;

export const useUnsavedAlertPrompt = (
  when: boolean,
  ignoreWhen?: (blocker?: Blocker) => boolean
) => {
  const { t } = useTranslation(['crm', 'shared']);
  const blocker = useBlocker(when);

  const { openConfirmModal, closeModal } = useModals();

  const openUnsavedModal = useCallback(
    ({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) => {
      return openConfirmModal({
        title: t('crm.unsavedAlertPrompt.title', { ns: 'crm' }),
        children: t('crm.unsavedAlertPrompt.desc', { ns: 'crm' }),
        labels: {
          confirm: t('shared.button.leavePage', { ns: 'shared' }),
        },
        onConfirm,
        onCancel,
      });
    },
    [openConfirmModal, t]
  );

  useEffect(() => {
    if (blocker.state === 'blocked' && !when) {
      blocker.reset();
    }
  }, [blocker, when]);

  useEffect(() => {
    let dialogId: string | undefined;

    if (ignoreWhen?.(blocker)) {
      return blocker?.proceed?.();
    }

    if (blocker.state === 'blocked') {
      dialogId = openUnsavedModal({
        onConfirm: () => {
          blocker.proceed();
        },
        onCancel: () => {
          blocker.reset();
        },
      });
    }

    return () => {
      if (dialogId) closeModal(dialogId);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker]);
};
